import { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  Snackbar,
  IconButton,
  CircularProgress,
  Box,
  Chip,
  Typography,
  Fade,
  Grow,
  Slide,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  CardHeader,
  Avatar,
  Divider,
  TablePagination,
  InputBase,
  Grid,
  Tab,
  Tabs,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  FormControlLabel,
  Switch,
} from "@mui/material";

import {
  Search,
  Close,
  CheckCircle,
  Warning,
  Info,
  Error,
  Fastfood,
  Save,
  Cancel,
  TrendingUp,
  TrendingDown,
  PlaylistAddCheck,
  History,
  Delete,
  Assessment,
  Download,
  Visibility,
  MoreVert,
  MoreHoriz,
  ViewList,
  ViewModule,
  Inventory,
  AttachMoney,
  Difference,
  Description,
  PictureAsPdf,
  CalendarToday,
  Timeline,
  Category,
  TrendingFlat,
} from "@mui/icons-material";

import { DateTimePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import frLocale from "date-fns/locale/fr";
import { collection, addDoc, updateDoc, deleteDoc, doc, getDocs, query, where, orderBy, serverTimestamp, Timestamp } from "firebase/firestore";
import { db } from "../../Firebase/Config";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

// ==================== COMPOSANTS STYLISÉS ====================

const StyledDialog = ({ children, open, onClose, title, icon: Icon, maxWidth = "sm", actions }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth={maxWidth}
      fullWidth
      TransitionComponent={Grow}
      TransitionProps={{ timeout: 400 }}
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 3,
          overflow: "hidden",
          background: "linear-gradient(135deg, #ffffff 0%, #fafafa 100%)",
          boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)"
        }
      }}
    >
      <Box sx={{ 
        background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
        px: 3,
        py: 2,
        display: "flex",
        alignItems: "center",
        gap: 1.5
      }}>
        {Icon && (
          <Avatar sx={{ bgcolor: "rgba(255,255,255,0.1)", color: "white" }}>
            <Icon />
          </Avatar>
        )}
        <Typography variant="h6" sx={{ color: "white", fontWeight: 600 }}>
          {title}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{ 
            ml: "auto",
            color: "white",
            "&:hover": { bgcolor: "rgba(255,255,255,0.1)" }
          }}
        >
          <Close />
        </IconButton>
      </Box>
      <Box sx={{ p: 3 }}>
        {children}
      </Box>
      {actions && (
        <Box sx={{ p: 2, borderTop: "1px solid", borderColor: "divider" }}>
          {actions}
        </Box>
      )}
    </Dialog>
  );
};

const StyledSnackbar = ({ open, onClose, message, severity, duration = 4000 }) => {
  const icons = {
    success: <CheckCircle sx={{ fontSize: 20 }} />,
    error: <Error sx={{ fontSize: 20 }} />,
    warning: <Warning sx={{ fontSize: 20 }} />,
    info: <Info sx={{ fontSize: 20 }} />
  };

  const colors = {
    success: "#10b981",
    error: "#ef4444",
    warning: "#f59e0b",
    info: "#3b82f6"
  };

  return (
    <Snackbar
      open={open}
      autoHideDuration={duration}
      onClose={onClose}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      TransitionComponent={Slide}
      TransitionProps={{ direction: "left" }}
    >
      <Paper
        elevation={6}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          p: 2,
          minWidth: 320,
          maxWidth: 400,
          borderRadius: 2,
          borderLeft: `4px solid ${colors[severity]}`,
          background: "white",
          boxShadow: "0 10px 40px rgba(0,0,0,0.1)",
        }}
      >
        <Box sx={{ 
          width: 40, 
          height: 40, 
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: `rgba(${severity === "success" ? "16,185,129" : severity === "error" ? "239,68,68" : severity === "warning" ? "245,158,11" : "59,130,246"}, 0.1)`,
          color: colors[severity]
        }}>
          {icons[severity]}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
            {severity === "success" ? "Succès" : 
             severity === "error" ? "Erreur" : 
             severity === "warning" ? "Attention" : "Information"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {message}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}>
          <Close fontSize="small" />
        </IconButton>
      </Paper>
    </Snackbar>
  );
};

const StatCard = ({ title, value, icon: Icon, color, trend, subtitle, onClick }) => {
  return (
    <Card 
      sx={{ 
        borderRadius: 3,
        cursor: onClick ? "pointer" : "default",
        transition: "all 0.3s ease",
        "&:hover": onClick ? {
          transform: "translateY(-4px)",
          boxShadow: "0 12px 24px rgba(0,0,0,0.1)"
        } : {}
      }}
      onClick={onClick}
    >
      <CardContent>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
            {trend && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 1 }}>
                {trend > 0 ? (
                  <TrendingUp sx={{ fontSize: 16, color: "#10b981" }} />
                ) : trend < 0 ? (
                  <TrendingDown sx={{ fontSize: 16, color: "#ef4444" }} />
                ) : null}
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: trend > 0 ? "#10b981" : trend < 0 ? "#ef4444" : "text.secondary",
                    fontWeight: 600
                  }}
                >
                  {trend > 0 ? "+" : ""}{trend}%
                </Typography>
              </Box>
            )}
          </Box>
          <Avatar sx={{ bgcolor: `rgba(${color === "#2563eb" ? "37,99,235" : color === "#10b981" ? "16,185,129" : color === "#f59e0b" ? "245,158,11" : "239,68,68"}, 0.1)`, color }}>
            <Icon />
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );
};

// ==================== COMPOSANT PRINCIPAL ====================

const InventairePage = () => {
  const theme = useTheme();

  // États principaux
  const [loading, setLoading] = useState(true);
  const [stockList, setStockList] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [viewMode, setViewMode] = useState("table");
  const [tabValue, setTabValue] = useState(0);

  // États inventaire
  const [inventories, setInventories] = useState([]);
  const [currentInventory, setCurrentInventory] = useState(null);
  const [inventoryMode, setInventoryMode] = useState(false);
  const [inventoryData, setInventoryData] = useState({});
  const [editingCell, setEditingCell] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [categories, setCategories] = useState([]);

  // États dialogs
  const [showNewInventoryDialog, setShowNewInventoryDialog] = useState(false);
  const [showInventoryDetails, setShowInventoryDetails] = useState(false);
  const [showStatsDialog, setShowStatsDialog] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(null);
  const [selectedInventory, setSelectedInventory] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);

  // États formulaire
  const [newInventoryForm, setNewInventoryForm] = useState({
    name: "",
    type: "full",
    date: new Date(),
    notes: "",
    includeAll: true,
    selectedCategories: []
  });

  // États snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });

  const restaurantId = localStorage.getItem("restaurantId");

  // ==================== CHARGEMENT DES DONNÉES DEPUIS FIREBASE ====================

  const loadStocks = async () => {
    try {
      const stocksRef = collection(db, "stocks");
      const q = query(stocksRef, where("restaurantId", "==", restaurantId));
      const querySnapshot = await getDocs(q);
      
      const stocks = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setStockList(stocks);
      
      // Extraire les catégories
      const cats = [...new Set(stocks.map(s => s.category).filter(Boolean))];
      setCategories(cats);
      
    } catch (error) {
      console.error("Error loading stocks:", error);
      showSnackbar("Erreur lors du chargement des stocks", "error");
    }
  };

  const loadInventories = async () => {
    try {
      const inventoriesRef = collection(db, "inventories");
      // Create a simpler query without orderBy to avoid index requirement
      const q = query(
        inventoriesRef, 
        where("restaurantId", "==", restaurantId)
      );
      const querySnapshot = await getDocs(q);
      
      const inventories = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Sort client-side
      inventories.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
        const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
        return dateB - dateA;
      });
      
      setInventories(inventories);
      
    } catch (error) {
      console.error("Error loading inventories:", error);
      showSnackbar("Erreur lors du chargement des inventaires", "error");
    }
  };

  const loadData = async () => {
    if (!restaurantId) return;
    
    try {
      setLoading(true);
      await Promise.all([loadStocks(), loadInventories()]);
    } catch (error) {
      console.error("Error loading data:", error);
      showSnackbar("Erreur lors du chargement des données", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (restaurantId) {
      loadData();
    }
  }, [restaurantId]);

  useEffect(() => {
    filterStocks();
  }, [searchTerm, stockList, selectedCategory]);

  const filterStocks = () => {
    let filtered = [...stockList];
    
    if (searchTerm) {
      filtered = filtered.filter(stock =>
        stock.productName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        stock.reference?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (selectedCategory !== "all") {
      filtered = filtered.filter(stock => stock.category === selectedCategory);
    }
    
    setFilteredList(filtered);
    setPage(0);
  };

  // ==================== GESTION INVENTAIRE AVEC FIREBASE ====================

  const startNewInventory = () => {
    setShowNewInventoryDialog(true);
  };

  const createInventory = async () => {
    try {
      // Validate name
      if (!newInventoryForm.name.trim()) {
        showSnackbar("Veuillez saisir un nom pour l'inventaire", "error");
        return;
      }

      let items = [];
      
      if (newInventoryForm.includeAll) {
        items = stockList.map(stock => ({
          id: stock.id || "",
          productName: stock.productName || "",
          reference: stock.reference || "",
          category: stock.category || "",
          theoretical: stock.quantity || 0,
          unit: stock.unit || "",
          pricePerUnit: stock.pricePerUnit || 0,
          counted: stock.quantity || 0,
          location: stock.location || ""
        }));
      } else {
        items = stockList
          .filter(stock => newInventoryForm.selectedCategories.includes(stock.category))
          .map(stock => ({
            id: stock.id || "",
            productName: stock.productName || "",
            reference: stock.reference || "",
            category: stock.category || "",
            theoretical: stock.quantity || 0,
            unit: stock.unit || "",
            pricePerUnit: stock.pricePerUnit || 0,
            counted: stock.quantity || 0,
            location: stock.location || ""
          }));
      }

      const inventoryDataToSave = {
        name: newInventoryForm.name.trim(),
        type: newInventoryForm.type,
        date: newInventoryForm.date.toISOString(),
        notes: newInventoryForm.notes || "",
        items: items,
        restaurantId: restaurantId,
        createdBy: localStorage.getItem("userName") || "Utilisateur",
        createdAt: serverTimestamp(),
        status: "in_progress",
        summary: null
      };

      const docRef = await addDoc(collection(db, "inventories"), inventoryDataToSave);
      
      const newInventory = {
        id: docRef.id,
        ...inventoryDataToSave,
        createdAt: new Date().toISOString()
      };

      setInventories(prev => [newInventory, ...prev]);
      setCurrentInventory(newInventory);
      setInventoryMode(true);
      
      // Initialiser les données d'inventaire
      const initialData = {};
      items.forEach(item => {
        initialData[item.id] = {
          ...item,
          counted: item.theoretical
        };
      });
      setInventoryData(initialData);
      
      setShowNewInventoryDialog(false);
      setTabValue(0);
      showSnackbar("Inventaire créé avec succès", "success");
      
    } catch (error) {
      console.error("Error creating inventory:", error);
      showSnackbar(`Erreur lors de la création: ${error.message}`, "error");
    }
  };

  const handleCountChange = (stockId, value) => {
    setInventoryData(prev => ({
      ...prev,
      [stockId]: {
        ...prev[stockId],
        counted: parseFloat(value) || 0
      }
    }));
  };

  const saveInventory = async () => {
    if (!currentInventory) return;

    setConfirmAction({
      title: "Terminer l'inventaire",
      message: "Voulez-vous finaliser cet inventaire ? Les stocks seront mis à jour avec les quantités comptées.",
      onConfirm: async () => {
        try {
          const items = Object.values(inventoryData);
          
          // Calculer les écarts
          const completedItems = items.map(item => ({
            ...item,
            variance: (item.counted || 0) - (item.theoretical || 0),
            varianceValue: ((item.counted || 0) - (item.theoretical || 0)) * (item.pricePerUnit || 0),
            status: item.counted === item.theoretical ? 'equal' : 
                    item.counted > item.theoretical ? 'surplus' : 'shortage'
          }));

          const summary = {
            totalItems: completedItems.length,
            totalTheoretical: completedItems.reduce((sum, i) => sum + (i.theoretical || 0), 0),
            totalCounted: completedItems.reduce((sum, i) => sum + (i.counted || 0), 0),
            totalVariance: completedItems.reduce((sum, i) => sum + (i.variance || 0), 0),
            totalVarianceValue: completedItems.reduce((sum, i) => sum + (i.varianceValue || 0), 0),
            itemsWithVariance: completedItems.filter(i => i.variance !== 0).length,
            surplusItems: completedItems.filter(i => i.variance > 0).length,
            shortageItems: completedItems.filter(i => i.variance < 0).length,
            totalValue: completedItems.reduce((sum, i) => sum + ((i.theoretical || 0) * (i.pricePerUnit || 0)), 0),
            countedValue: completedItems.reduce((sum, i) => sum + ((i.counted || 0) * (i.pricePerUnit || 0)), 0),
          };

          // Mettre à jour l'inventaire dans Firebase
          const inventoryRef = doc(db, "inventories", currentInventory.id);
          await updateDoc(inventoryRef, {
            items: completedItems,
            summary,
            status: 'completed',
            completedAt: serverTimestamp(),
            completedBy: localStorage.getItem("userName") || "Utilisateur"
          });

          // Mettre à jour les stocks dans Firebase
          for (const item of completedItems) {
            if (item.variance !== 0 && item.id) {
              const stockRef = doc(db, "stocks", item.id);
              await updateDoc(stockRef, {
                quantity: item.counted,
                date: new Date().toISOString()
              });
            }
          }
          
          setInventories(prev => prev.map(inv => 
            inv.id === currentInventory.id ? { ...inv, items: completedItems, summary, status: 'completed' } : inv
          ));
          
          setInventoryMode(false);
          setCurrentInventory(null);
          setInventoryData({});
          setShowConfirmDialog(false);
          
          showSnackbar("Inventaire terminé avec succès", "success");
          
          // Recharger les stocks
          await loadStocks();
        } catch (error) {
          console.error("Error saving inventory:", error);
          showSnackbar(`Erreur lors de la sauvegarde: ${error.message}`, "error");
        }
      }
    });
    setShowConfirmDialog(true);
  };

  const cancelInventory = () => {
    setConfirmAction({
      title: "Annuler l'inventaire",
      message: "Voulez-vous vraiment annuler ? Les modifications non sauvegardées seront perdues.",
      onConfirm: async () => {
        try {
          if (currentInventory) {
            const inventoryRef = doc(db, "inventories", currentInventory.id);
            await updateDoc(inventoryRef, {
              status: 'cancelled',
              cancelledAt: serverTimestamp()
            });
          }
        } catch (error) {
          console.error("Error cancelling inventory:", error);
        }
        
        setInventoryMode(false);
        setCurrentInventory(null);
        setInventoryData({});
        setShowConfirmDialog(false);
        showSnackbar("Inventaire annulé", "info");
      }
    });
    setShowConfirmDialog(true);
  };

  const viewInventoryDetails = (inventory) => {
    setSelectedInventory(inventory);
    setShowInventoryDetails(true);
  };

  const deleteInventory = async (inventory) => {
    setConfirmAction({
      title: "Supprimer l'inventaire",
      message: "Voulez-vous vraiment supprimer cet inventaire ? Cette action est irréversible.",
      onConfirm: async () => {
        try {
          const inventoryRef = doc(db, "inventories", inventory.id);
          await deleteDoc(inventoryRef);
          
          setInventories(prev => prev.filter(inv => inv.id !== inventory.id));
          setShowConfirmDialog(false);
          setShowInventoryDetails(false);
          showSnackbar("Inventaire supprimé", "success");
        } catch (error) {
          console.error("Error deleting inventory:", error);
          showSnackbar("Erreur lors de la suppression", "error");
        }
      }
    });
    setShowConfirmDialog(true);
  };

  // ==================== CALCULS ====================

  const calculateVariance = (stockId) => {
    if (!inventoryData[stockId]) return 0;
    return inventoryData[stockId].counted - inventoryData[stockId].theoretical;
  };

  const getVarianceColor = (variance) => {
    if (variance > 0) return "#10b981";
    if (variance < 0) return "#ef4444";
    return "#6b7280";
  };

  const getInventoryStats = () => {
    const completed = inventories.filter(inv => inv.status === 'completed');
    const totalVariance = completed.reduce((sum, inv) => sum + (inv.summary?.totalVarianceValue || 0), 0);
    
    return {
      total: inventories.length,
      completed: completed.length,
      draft: inventories.filter(inv => inv.status === 'draft').length,
      inProgress: inventories.filter(inv => inv.status === 'in_progress').length,
      totalVariance: totalVariance,
      averageVariance: completed.length ? totalVariance / completed.length : 0,
      lastInventory: completed.length ? completed[completed.length - 1] : null,
    };
  };

  // ==================== EXPORT ====================

  const exportToExcel = (inventory) => {
    try {
      const data = inventory.items?.map(item => ({
        'Produit': item.productName || '',
        'Référence': item.reference || '',
        'Catégorie': item.category || '',
        'Unité': item.unit || '',
        'Prix unitaire': item.pricePerUnit || 0,
        'Stock théorique': item.theoretical || 0,
        'Stock compté': item.counted || 0,
        'Écart': item.variance || 0,
        'Valeur écart': ((item.variance || 0) * (item.pricePerUnit || 0)).toFixed(3),
        'Emplacement': item.location || ''
      })) || [];

      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Inventaire');
      
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      
      saveAs(blob, `inventaire_${inventory.name}_${formatDate(inventory.completedAt)}.xlsx`);
      
      showSnackbar("Export Excel réussi", "success");
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      showSnackbar("Erreur lors de l'export", "error");
    }
  };

  const exportToPDF = (inventory) => {
    try {
      const doc = new jsPDF();
      
      doc.setFontSize(18);
      doc.text('Rapport d\'inventaire', 14, 22);
      
      doc.setFontSize(11);
      doc.text(`Inventaire: ${inventory.name || ''}`, 14, 32);
      doc.text(`Date: ${formatDate(inventory.completedAt)}`, 14, 38);
      doc.text(`Créé par: ${inventory.createdBy || ''}`, 14, 44);
      
      const tableColumn = ["Produit", "Théorique", "Compté", "Écart", "Valeur"];
      const tableRows = inventory.items?.map(item => [
        item.productName || '',
        item.theoretical || 0,
        item.counted || 0,
        item.variance || 0,
        `${((item.variance || 0) * (item.pricePerUnit || 0)).toFixed(3)} TND`
      ]) || [];
      
      doc.autoTable({
        startY: 50,
        head: [tableColumn],
        body: tableRows,
        theme: 'striped',
        styles: { fontSize: 8 },
        headStyles: { fillColor: [37, 99, 235] }
      });
      
      doc.save(`inventaire_${inventory.name}_${formatDate(inventory.completedAt)}.pdf`);
      
      showSnackbar("Export PDF réussi", "success");
    } catch (error) {
      console.error("Error exporting to PDF:", error);
      showSnackbar("Erreur lors de l'export", "error");
    }
  };

  // ==================== FORMATAGE ====================

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      // Handle Firestore Timestamp
      let date;
      if (dateString.toDate && typeof dateString.toDate === 'function') {
        date = dateString.toDate();
      } else {
        date = new Date(dateString);
      }
      if (isNaN(date.getTime())) return '';
      
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return '';
    }
  };

  const formatNumber = (num) => {
    if (num === undefined || num === null) return '0';
    return num.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 3 });
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  // ==================== PAGINATION ====================

  const paginatedList = filteredList.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const stats = getInventoryStats();

  // ==================== RENDU ====================

  if (loading) {
    return (
      <Box sx={{ 
        p: 4, 
        display: "flex", 
        flexDirection: "column", 
        alignItems: "center", 
        justifyContent: "center", 
        minHeight: "100vh",
        bgcolor: "#f3f4f6"
      }}>
        <CircularProgress size={60} thickness={4} sx={{ color: "#2563eb", mb: 3 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Chargement de l'application...
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Récupération des données d'inventaire
        </Typography>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={frLocale}>
      <Box sx={{ 
        minHeight: "100vh",
        bgcolor: "#f3f4f6",
      }}>
        {/* Header principal */}
        <Paper 
          sx={{ 
            borderRadius: 0,
            bgcolor: "white",
            borderBottom: "1px solid",
            borderColor: "divider",
            position: "sticky",
            top: 0,
            zIndex: 10
          }}
        >
          <Box sx={{ p: 2, maxWidth: 1400, margin: "auto", width: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
              <Avatar sx={{ bgcolor: "#2563eb", width: 48, height: 48 }}>
                <Inventory />
              </Avatar>
              
              <Box sx={{ flex: 1 }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  Gestion des Inventaires
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {stockList.length} produits · {inventories.length} inventaires
                </Typography>
              </Box>

              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="contained"
                  startIcon={<PlaylistAddCheck />}
                  onClick={startNewInventory}
                  sx={{ borderRadius: 2 }}
                >
                  Nouvel inventaire
                </Button>
                
                <IconButton 
                  onClick={(e) => setShowExportMenu(e.currentTarget)}
                  sx={{ bgcolor: "rgba(0,0,0,0.04)" }}
                >
                  <MoreVert />
                </IconButton>
                
                <Menu
                  anchorEl={showExportMenu}
                  open={Boolean(showExportMenu)}
                  onClose={() => setShowExportMenu(null)}
                >
                  <MenuItem onClick={() => {
                    setShowStatsDialog(true);
                    setShowExportMenu(null);
                  }}>
                    <ListItemIcon><Assessment fontSize="small" /></ListItemIcon>
                    <ListItemText>Statistiques</ListItemText>
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={() => {
                    exportToExcel({ items: stockList, name: "stock_actuel" });
                    setShowExportMenu(null);
                  }}>
                    <ListItemIcon><Download fontSize="small" /></ListItemIcon>
                    <ListItemText>Exporter stock actuel</ListItemText>
                  </MenuItem>
                </Menu>
              </Box>
            </Box>

            {/* Tabs */}
            <Tabs 
              value={tabValue} 
              onChange={(e, v) => setTabValue(v)}
              sx={{ mt: 2 }}
            >
              <Tab label="Inventaire en cours" />
              <Tab label="Historique" />
              <Tab label="Statistiques" />
            </Tabs>
          </Box>
        </Paper>

        {/* Contenu principal */}
        <Box sx={{ p: 3, maxWidth: 1400, margin: "auto" }}>
          {/* Stats Cards */}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr 1fr" }, gap: 2, mb: 3 }}>
            <StatCard
              title="Total produits"
              value={stockList.length}
              icon={Fastfood}
              color="#2563eb"
            />
            <StatCard
              title="Valeur stock"
              value={`${formatNumber(stockList.reduce((sum, s) => sum + ((s.pricePerUnit || 0) * (s.quantity || 0)), 0))} TND`}
              icon={AttachMoney}
              color="#10b981"
            />
            <StatCard
              title="Inventaires"
              value={stats.completed}
              subtitle={`${stats.draft} brouillons`}
              icon={History}
              color="#f59e0b"
            />
            <StatCard
              title="Écart total"
              value={`${formatNumber(stats.totalVariance)} TND`}
              icon={Difference}
              color={stats.totalVariance > 0 ? "#10b981" : "#ef4444"}
              trend={stats.completed ? (stats.totalVariance / stats.completed) : 0}
            />
          </Box>

          {/* Mode inventaire actif */}
          {inventoryMode && currentInventory && (
            <Fade in>
              <Paper sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: "#e3f2fd" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                  <Avatar sx={{ bgcolor: "#2563eb" }}>
                    <PlaylistAddCheck />
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {currentInventory.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {Object.keys(inventoryData).length} articles · Démarré le {formatDate(currentInventory.createdAt)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<Save />}
                      onClick={saveInventory}
                      sx={{ borderRadius: 2 }}
                    >
                      Terminer
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<Cancel />}
                      onClick={cancelInventory}
                      sx={{ borderRadius: 2 }}
                    >
                      Annuler
                    </Button>
                  </Box>
                </Box>
              </Paper>
            </Fade>
          )}

          {/* Barre de recherche et filtres */}
          <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
              <Paper
                component="form"
                sx={{
                  p: "2px 4px",
                  display: "flex",
                  alignItems: "center",
                  flex: 1,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2
                }}
              >
                <IconButton sx={{ p: "10px" }}>
                  <Search />
                </IconButton>
                <InputBase
                  sx={{ ml: 1, flex: 1 }}
                  placeholder="Rechercher un produit par nom ou référence..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <IconButton sx={{ p: "10px" }} onClick={() => setSearchTerm("")}>
                    <Close />
                  </IconButton>
                )}
              </Paper>

              <FormControl sx={{ minWidth: 150 }}>
                <InputLabel>Catégorie</InputLabel>
                <Select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  label="Catégorie"
                >
                  <MenuItem value="all">Toutes les catégories</MenuItem>
                  {categories.map(cat => (
                    <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <IconButton onClick={() => setViewMode(viewMode === "table" ? "cards" : "table")}>
                {viewMode === "table" ? <ViewModule /> : <ViewList />}
              </IconButton>
            </Box>
          </Paper>

          {/* Contenu selon l'onglet */}
          {tabValue === 0 && (
            <Fade in>
              <Box>
                {inventoryMode ? (
                  // Table d'inventaire
                  <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: "#f8fafc" }}>
                          <TableCell>Produit</TableCell>
                          <TableCell>Catégorie</TableCell>
                          <TableCell>Unité</TableCell>
                          <TableCell align="right">Stock théorique</TableCell>
                          <TableCell align="right">Stock compté</TableCell>
                          <TableCell align="center">Écart</TableCell>
                          <TableCell align="right">Valeur écart</TableCell>
                          <TableCell align="right">Emplacement</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedList.map((stock) => {
                          const variance = calculateVariance(stock.id);
                          const varianceValue = variance * (stock.pricePerUnit || 0);
                          
                          return (
                            <TableRow key={stock.id} hover>
                              <TableCell>
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {stock.productName || ''}
                                  </Typography>
                                  {stock.reference && (
                                    <Typography variant="caption" color="text.secondary">
                                      Réf: {stock.reference}
                                    </Typography>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Chip 
                                  label={stock.category || "Non catégorisé"} 
                                  size="small"
                                  sx={{ bgcolor: "rgba(37,99,235,0.1)" }}
                                />
                              </TableCell>
                              <TableCell>{stock.unit || '-'}</TableCell>
                              <TableCell align="right">
                                <Chip
                                  label={stock.quantity || 0}
                                  size="small"
                                  sx={{ bgcolor: "#e3f2fd", color: "#1976d2" }}
                                />
                              </TableCell>
                              <TableCell align="right">
                                <TextField
                                  size="small"
                                  type="number"
                                  value={inventoryData[stock.id]?.counted !== undefined ? inventoryData[stock.id].counted : (stock.quantity || 0)}
                                  onChange={(e) => handleCountChange(stock.id, e.target.value)}
                                  onClick={() => setEditingCell(stock.id)}
                                  autoFocus={editingCell === stock.id}
                                  sx={{ width: 100 }}
                                  inputProps={{ style: { textAlign: "right" } }}
                                />
                              </TableCell>
                              <TableCell align="center">
                                {variance !== 0 && (
                                  <Chip
                                    icon={variance > 0 ? <TrendingUp /> : <TrendingDown />}
                                    label={variance > 0 ? `+${variance}` : variance}
                                    size="small"
                                    sx={{
                                      bgcolor: `rgba(${getVarianceColor(variance) === "#10b981" ? "16,185,129" : getVarianceColor(variance) === "#ef4444" ? "239,68,68" : "107,114,128"}, 0.1)`,
                                      color: getVarianceColor(variance),
                                      fontWeight: 600
                                    }}
                                  />
                                )}
                              </TableCell>
                              <TableCell align="right">
                                <Typography
                                  sx={{
                                    fontWeight: 600,
                                    color: getVarianceColor(variance)
                                  }}
                                >
                                  {variance !== 0 ? `${variance > 0 ? "+" : ""}${varianceValue.toFixed(3)} TND` : "-"}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                {stock.location || "-"}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                    
                    <TablePagination
                      rowsPerPageOptions={[10, 25, 50, 100]}
                      component="div"
                      count={filteredList.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={(e, newPage) => setPage(newPage)}
                      onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                      }}
                      labelRowsPerPage="Lignes par page"
                    />
                  </TableContainer>
                ) : (
                  // Vue normale - Liste des inventaires récents
                  <Box>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      Inventaires récents
                    </Typography>
                    
                    <Box sx={{ display: "grid", gap: 2 }}>
                      {inventories.filter(inv => inv.status !== 'draft' && inv.status !== 'in_progress').slice(0, 5).map((inventory) => (
                        <Card key={inventory.id} sx={{ borderRadius: 2 }}>
                          <CardContent>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
                              <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                  {inventory.name}
                                </Typography>
                                <Box sx={{ display: "flex", gap: 2, mt: 1, flexWrap: "wrap" }}>
                                  <Chip
                                    size="small"
                                    label={formatDate(inventory.completedAt || inventory.createdAt)}
                                    icon={<CalendarToday />}
                                  />
                                  <Chip
                                    size="small"
                                    label={`${inventory.items?.length || 0} articles`}
                                  />
                                  {inventory.summary && (
                                    <>
                                      <Chip
                                        size="small"
                                        label={`Écart: ${formatNumber(inventory.summary.totalVarianceValue)} TND`}
                                        sx={{
                                          bgcolor: inventory.summary.totalVarianceValue > 0 ? "rgba(16,185,129,0.1)" : 
                                                 inventory.summary.totalVarianceValue < 0 ? "rgba(239,68,68,0.1)" : 
                                                 "rgba(107,114,128,0.1)"
                                        }}
                                      />
                                      <Chip
                                        size="small"
                                        label={`${inventory.summary.itemsWithVariance} écarts`}
                                        sx={{ bgcolor: "rgba(245,158,11,0.1)" }}
                                      />
                                    </>
                                  )}
                                </Box>
                              </Box>
                              <Box>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => viewInventoryDetails(inventory)}
                                  startIcon={<Visibility />}
                                  sx={{ mr: 1 }}
                                >
                                  Détails
                                </Button>
                                <IconButton 
                                  size="small"
                                  onClick={(e) => {
                                    setSelectedInventory(inventory);
                                    setShowExportMenu(e.currentTarget);
                                  }}
                                >
                                  <MoreHoriz />
                                </IconButton>
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      ))}
                      
                      {inventories.filter(inv => inv.status !== 'draft' && inv.status !== 'in_progress').length === 0 && (
                        <Paper sx={{ p: 4, textAlign: "center" }}>
                          <History sx={{ fontSize: 50, color: "#ccc", mb: 2 }} />
                          <Typography color="text.secondary">
                            Aucun inventaire terminé
                          </Typography>
                          <Button
                            variant="contained"
                            startIcon={<PlaylistAddCheck />}
                            onClick={startNewInventory}
                            sx={{ mt: 2 }}
                          >
                            Commencer un inventaire
                          </Button>
                        </Paper>
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
            </Fade>
          )}

          {tabValue === 1 && (
            <Fade in>
              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Historique complet des inventaires
                </Typography>
                
                <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: "#f8fafc" }}>
                        <TableCell>Date</TableCell>
                        <TableCell>Nom</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell align="right">Articles</TableCell>
                        <TableCell align="right">Écarts</TableCell>
                        <TableCell align="right">Valeur écart</TableCell>
                        <TableCell>Créé par</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {inventories.filter(inv => inv.status !== 'draft' && inv.status !== 'in_progress').map((inventory) => (
                        <TableRow key={inventory.id} hover>
                          <TableCell>
                            {formatDate(inventory.completedAt || inventory.createdAt)}
                          </TableCell>
                          <TableCell>
                            <Typography sx={{ fontWeight: 500 }}>
                              {inventory.name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={inventory.type === 'full' ? 'Complet' : 'Partiel'} 
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="right">
                            {inventory.items?.length || 0}
                          </TableCell>
                          <TableCell align="right">
                            {inventory.summary?.itemsWithVariance || 0}
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              sx={{
                                color: inventory.summary?.totalVarianceValue > 0 ? "#10b981" : 
                                       inventory.summary?.totalVarianceValue < 0 ? "#ef4444" : "inherit",
                                fontWeight: 600
                              }}
                            >
                              {formatNumber(inventory.summary?.totalVarianceValue)} TND
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {inventory.createdBy || "-"}
                          </TableCell>
                          <TableCell align="center">
                            <IconButton 
                              size="small"
                              onClick={() => viewInventoryDetails(inventory)}
                            >
                              <Visibility />
                            </IconButton>
                            <IconButton 
                              size="small"
                              onClick={() => exportToExcel(inventory)}
                            >
                              <Download />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Fade>
          )}

          {tabValue === 2 && (
            <Fade in>
              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Statistiques des inventaires
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Card sx={{ borderRadius: 2 }}>
                      <CardHeader
                        avatar={<Assessment />}
                        title="Résumé global"
                      />
                      <CardContent>
                        <Box sx={{ display: "grid", gap: 2 }}>
                          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                            <Typography color="text.secondary">Total inventaires:</Typography>
                            <Typography sx={{ fontWeight: 600 }}>{stats.total}</Typography>
                          </Box>
                          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                            <Typography color="text.secondary">Terminés:</Typography>
                            <Typography sx={{ fontWeight: 600 }}>{stats.completed}</Typography>
                          </Box>
                          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                            <Typography color="text.secondary">En cours:</Typography>
                            <Typography sx={{ fontWeight: 600 }}>{stats.inProgress}</Typography>
                          </Box>
                          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                            <Typography color="text.secondary">Brouillons:</Typography>
                            <Typography sx={{ fontWeight: 600 }}>{stats.draft}</Typography>
                          </Box>
                          <Divider />
                          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                            <Typography color="text.secondary">Écart total:</Typography>
                            <Typography 
                              sx={{ 
                                fontWeight: 600,
                                color: stats.totalVariance > 0 ? "#10b981" : 
                                       stats.totalVariance < 0 ? "#ef4444" : "inherit"
                              }}
                            >
                              {formatNumber(stats.totalVariance)} TND
                            </Typography>
                          </Box>
                          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                            <Typography color="text.secondary">Écart moyen:</Typography>
                            <Typography sx={{ fontWeight: 600 }}>
                              {formatNumber(stats.averageVariance)} TND
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Card sx={{ borderRadius: 2 }}>
                      <CardHeader
                        avatar={<Timeline />}
                        title="Évolution des écarts"
                      />
                      <CardContent>
                        <Box sx={{ height: 200, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <Typography color="text.secondary">
                            Graphique d'évolution des écarts (bientôt disponible)
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  
               
                </Grid>
              </Box>
            </Fade>
          )}
        </Box>

        {/* ==================== DIALOGS ==================== */}

        {/* Dialog Nouvel Inventaire */}
        <StyledDialog
          open={showNewInventoryDialog}
          onClose={() => setShowNewInventoryDialog(false)}
          title="Nouvel inventaire"
          icon={PlaylistAddCheck}
          maxWidth="md"
          actions={
            <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
              <Button onClick={() => setShowNewInventoryDialog(false)}>
                Annuler
              </Button>
              <Button
                variant="contained"
                onClick={createInventory}
                startIcon={<PlaylistAddCheck />}
              >
                Commencer
              </Button>
            </Box>
          }
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nom de l'inventaire"
                value={newInventoryForm.name}
                onChange={(e) => setNewInventoryForm({ ...newInventoryForm, name: e.target.value })}
                required
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Type d'inventaire</InputLabel>
                <Select
                  value={newInventoryForm.type}
                  onChange={(e) => setNewInventoryForm({ ...newInventoryForm, type: e.target.value })}
                  label="Type d'inventaire"
                >
                  <MenuItem value="full">Inventaire complet</MenuItem>
                  <MenuItem value="partial">Inventaire partiel</MenuItem>
                  <MenuItem value="spot">Inventaire spot</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <DateTimePicker
                label="Date prévue"
                value={newInventoryForm.date}
                onChange={(date) => setNewInventoryForm({ ...newInventoryForm, date })}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={newInventoryForm.includeAll}
                    onChange={(e) => setNewInventoryForm({ 
                      ...newInventoryForm, 
                      includeAll: e.target.checked,
                      selectedCategories: e.target.checked ? [] : newInventoryForm.selectedCategories
                    })}
                  />
                }
                label="Inclure tous les produits"
              />
            </Grid>
            
            {!newInventoryForm.includeAll && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Catégories à inclure</InputLabel>
                  <Select
                    multiple
                    value={newInventoryForm.selectedCategories}
                    onChange={(e) => setNewInventoryForm({ 
                      ...newInventoryForm, 
                      selectedCategories: e.target.value 
                    })}
                    label="Catégories à inclure"
                    renderValue={(selected) => selected.join(', ')}
                  >
                    {categories.map(cat => (
                      <MenuItem key={cat} value={cat}>
                        <Checkbox checked={newInventoryForm.selectedCategories.includes(cat)} />
                        <ListItemText primary={cat} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Notes"
                value={newInventoryForm.notes}
                onChange={(e) => setNewInventoryForm({ ...newInventoryForm, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </StyledDialog>

        {/* Dialog Détails Inventaire */}
        <StyledDialog
          open={showInventoryDetails}
          onClose={() => setShowInventoryDetails(false)}
          title={`Inventaire: ${selectedInventory?.name || ''}`}
          icon={Description}
          maxWidth="lg"
        >
           <Box
    sx={{
      maxHeight: 'calc(100vh - 200px)', // Adjust height based on screen size
      overflowY: 'auto',
      overflowX: 'hidden',
      paddingRight: 1, // Add some padding for scrollbar
      '&::-webkit-scrollbar': {
        width: '8px',
      },
      '&::-webkit-scrollbar-track': {
        backgroundColor: '#f1f1f1',
        borderRadius: '4px',
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: '#c1c1c1',
        borderRadius: '4px',
        '&:hover': {
          backgroundColor: '#a8a8a8',
        },
      },
    }}
  >
          {selectedInventory && (
            <Box>
              {/* En-tête */}
              <Paper sx={{ p: 2, mb: 3, bgcolor: "#f8fafc", borderRadius: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">Date</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {formatDate(selectedInventory.completedAt || selectedInventory.createdAt)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">Type</Typography>
                    <Chip 
                      label={selectedInventory.type === 'full' ? 'Complet' : 'Partiel'} 
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">Créé par</Typography>
                    <Typography variant="body1">{selectedInventory.createdBy || "-"}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">Statut</Typography>
                    <Chip 
                      label={selectedInventory.status === 'completed' ? 'Terminé' : 'Brouillon'}
                      color={selectedInventory.status === 'completed' ? 'success' : 'default'}
                      size="small"
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* Résumé */}
              {selectedInventory.summary && (
                <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                    Résumé
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="text.secondary">Total articles</Typography>
                      <Typography variant="h6">{selectedInventory.summary.totalItems}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="text.secondary">Articles avec écart</Typography>
                      <Typography variant="h6" color={selectedInventory.summary.itemsWithVariance > 0 ? "#f59e0b" : "inherit"}>
                        {selectedInventory.summary.itemsWithVariance}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="text.secondary">Écart total</Typography>
                      <Typography 
                        variant="h6"
                        sx={{
                          color: selectedInventory.summary.totalVarianceValue > 0 ? "#10b981" : 
                                 selectedInventory.summary.totalVarianceValue < 0 ? "#ef4444" : "inherit"
                        }}
                      >
                        {formatNumber(selectedInventory.summary.totalVarianceValue)} TND
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="text.secondary">Valeur totale</Typography>
                      <Typography variant="h6">{formatNumber(selectedInventory.summary.totalValue)} TND</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              )}

              {/* Liste des articles */}
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Détail des articles
              </Typography>
              
              <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Produit</TableCell>
                      <TableCell>Catégorie</TableCell>
                      <TableCell align="right">Théorique</TableCell>
                      <TableCell align="right">Compté</TableCell>
                      <TableCell align="center">Écart</TableCell>
                      <TableCell align="right">Valeur écart</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedInventory.items?.map((item) => {
                      const variance = (item.counted || 0) - (item.theoretical || 0);
                      return (
                        <TableRow key={item.id}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {item.productName || ''}
                            </Typography>
                            {item.reference && (
                              <Typography variant="caption" color="text.secondary">
                                {item.reference}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={item.category || "-"} 
                              size="small"
                              sx={{ bgcolor: "rgba(37,99,235,0.1)" }}
                            />
                          </TableCell>
                          <TableCell align="right">{item.theoretical || 0}</TableCell>
                          <TableCell align="right">
                            <Chip
                              label={item.counted || 0}
                              size="small"
                              sx={{ 
                                bgcolor: variance === 0 ? "#f5f5f5" : 
                                       variance > 0 ? "rgba(16,185,129,0.1)" : "rgba(239,68,68,0.1)"
                              }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            {variance !== 0 && (
                              <Chip
                                icon={variance > 0 ? <TrendingUp /> : <TrendingDown />}
                                label={variance > 0 ? `+${variance}` : variance}
                                size="small"
                                sx={{
                                  bgcolor: variance > 0 ? "rgba(16,185,129,0.1)" : "rgba(239,68,68,0.1)",
                                  color: variance > 0 ? "#10b981" : "#ef4444",
                                }}
                              />
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              sx={{
                                color: variance > 0 ? "#10b981" : variance < 0 ? "#ef4444" : "inherit",
                                fontWeight: 600
                              }}
                            >
                              {(variance * (item.pricePerUnit || 0)).toFixed(3)} TND
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Notes */}
              {selectedInventory.notes && (
                <Paper sx={{ p: 2, mt: 3, borderRadius: 2, bgcolor: "#f8fafc" }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Notes
                  </Typography>
                  <Typography variant="body1">
                    {selectedInventory.notes}
                  </Typography>
                </Paper>
              )}

              {/* Actions */}
              <Box sx={{ mt: 3, display: "flex", gap: 2, justifyContent: "flex-end" }}>
                <Button
                  variant="outlined"
                  startIcon={<Download />}
                  onClick={() => exportToExcel(selectedInventory)}
                >
                  Excel
                </Button>
               
                {selectedInventory.status !== 'completed' && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<Delete />}
                    onClick={() => deleteInventory(selectedInventory)}
                  >
                    Supprimer
                  </Button>
                )}
                <Button
                  variant="contained"
                  onClick={() => setShowInventoryDetails(false)}
                >
                  Fermer
                </Button>
              </Box>
            </Box>
          )}
          </Box>
        </StyledDialog>

        {/* Dialog Confirmation */}
        <Dialog
          open={showConfirmDialog}
          onClose={() => setShowConfirmDialog(false)}
          maxWidth="xs"
          fullWidth
        >
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              {confirmAction?.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {confirmAction?.message}
            </Typography>
            <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
              <Button onClick={() => setShowConfirmDialog(false)}>
                Annuler
              </Button>
              <Button
                variant="contained"
                color={confirmAction?.title?.includes("Supprimer") ? "error" : "primary"}
                onClick={() => {
                  confirmAction?.onConfirm();
                  setShowConfirmDialog(false);
                }}
              >
                Confirmer
              </Button>
            </Box>
          </Box>
        </Dialog>

        {/* Dialog Stats */}
        <Dialog
          open={showStatsDialog}
          onClose={() => setShowStatsDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Statistiques détaillées
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Fonctionnalité à venir...
            </Typography>
            <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
              <Button onClick={() => setShowStatsDialog(false)} variant="contained">
                Fermer
              </Button>
            </Box>
          </Box>
        </Dialog>

        {/* Snackbar */}
        <StyledSnackbar
          open={snackbar.open}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          message={snackbar.message}
          severity={snackbar.severity}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default InventairePage;